<template>
    <div class="details">
        <ProductDetailsHeader :product="product" />

        <Divider color="lightGrey" bottomMargin="xl" topMargin="m" thickness="1" />

        <div v-if="product.IsRebookable" class="details-labels">
            <RebookableBadge size="l" />
        </div>

        <div class="details-journey">
            <Journeys
                type="Flight"
                direction="column"
                :legs="product.Legs"
                :showDetailed="false"
                :showHeaderBackground="false"
                :showHeader="product.Legs.length > 1" />
        </div>

        <div class="details-actions">
            <ButtonLink size="m" color="black" :onClick="onFlightDetailsClick">
                {{ t({ key: 'shared.flight_details' }) }}
            </ButtonLink>

            <ButtonLink
                v-if="product && product.FlightTermsUrl !== null"
                size="m"
                color="black"
                :onClick="onFlightTermsClick">
                {{ t({ key: 'shared.ticket_policy' }) }}
            </ButtonLink>
        </div>

        <Divider v-if="!isDesktop" color="lightGrey" topMargin="xl" thickness="1" />

        <RyanairBookingProcess v-if="showRyanairProcess" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, computed, PropType } from 'vue';
    import { useStore } from '@/store';
    import { t } from '@shared/helpers/translation';
    import { FlightProduct } from '@/api/Cart/cart.interfaces';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import {
        logStatsigCheckoutMoreInformationOpen,
        logStatsigCheckoutTicketPolicyOpen,
    } from '@/tracking/statsig/checkout.functions';
    import { cartProductHasRyanair } from '@/helpers/cart';
    import RebookableBadge from '@/components/shared/RebookableBadge/RebookableBadge.vue';
    import Journeys from '@/components/shared/Journey/Journeys.vue';
    import ProductDetailsHeader from '@/components/checkoutPageV3/Shared/ProductDetailsHeader/ProductDetailsHeader.vue';
    import RyanairBookingProcess from '@/components/checkoutPageV3/Shared/RyanairBookingProcess/RyanairBookingProcess.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'FlightDetails',
        methods: { t, getMoneyObjectAsString },
        components: {
            ButtonLink,
            Divider,
            ProductDetailsHeader,
            RyanairBookingProcess,
            RebookableBadge,
            Journeys,
        },
        props: {
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
            showPrice: { type: Boolean, required: true },
        },
        setup(props) {
            const store = useStore();
            const { isDesktop } = useScreenSize();

            const showRyanairProcess = computed<boolean>(() =>
                cartProductHasRyanair(props.product)
            );

            function onFlightDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Details,
                });

                logStatsigCheckoutMoreInformationOpen(props.product.Type);
            }

            function onFlightTermsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Terms,
                });

                logStatsigCheckoutTicketPolicyOpen(props.product.Type);
            }

            return {
                isDesktop,
                showRyanairProcess,
                onFlightDetailsClick,
                onFlightTermsClick,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Details.scss';
</style>
